import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { checkIfSSOIsEnabled } from '../../auth-bundle/helpers/sso';
import Layout from '../../component/layout';
import { useMetadata } from '../../hooks/useMetadata';
import { initScripts, initWithoutMetadata } from '../../init';
import '../../stylesheets/application.scss';

import { TransferCustomizationProvider } from '@wetransfer/transfer/component/TransferCustomizationProvider';
import { useDisplayCustomWallpaper } from '@wetransfer/transfer/component/TransferCustomizationProvider/hooks';
import { useCurrentTeam } from '@wetransfer/transfer/hooks/useCurrentTeam/useCurrentTeam';
import Analytics from '@wetransfer/transfer/modules/analytics';
import { useExperiments } from '@wetransfer/transfer/modules/experiments/hooks';
import trackCustomer from '@wetransfer/transfer/modules/referral/events/trackCustomer';
import { State } from '@wetransfer/transfer/store/types';
import { getCurrentStorage } from '@wetransfer/transfer/transfer-bundle/store/selectors';

import { useIsMobileWebWallpaperEnabled } from './hooks';

export const TransferApp: React.FC = () => {
  const { route, auth, user, account, currentStorage } = useSelector(
    (state: State) => ({
      route: state.route,
      auth: state.auth,
      user: state.user,
      account: state.account,
      currentStorage: getCurrentStorage(state),
    })
  );

  const { isReady: isAmplitudeReady } = useExperiments();

  const { metadata, fetching: fetchingMetadata } = useMetadata();
  const { currentTeam, allTeams } = useCurrentTeam();

  const SSOEnabled = checkIfSSOIsEnabled();

  const isLoading = useMemo(
    () => (SSOEnabled && !auth.isInitialized) || fetchingMetadata,
    [SSOEnabled, auth.isInitialized, fetchingMetadata]
  );

  useDisplayCustomWallpaper();

  useEffect(() => {
    try {
      initWithoutMetadata();
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    if (metadata && !isLoading) {
      initScripts({
        metadata,
        userId: user.id,
        language: user.language,
        userLoggedIn: user.loggedIn,
        planTier: user.planTier,
        auth0UserId: auth?.auth0UserId,
        auth: auth,
        user: user,
        account: account,
        currentTeam: currentTeam,
        allTeams: allTeams,
        currentStorage,
      });

      if (user.loggedIn) {
        trackCustomer({
          id: user.id.toString(),
          email: user.email,
          firstName: user.given_name,
          lastName: user.family_name,
        });
      }
    }
    // We MUST not send entire objects like `user`/`auth`/`acount` here. That cause this effect to be re-run multiple times and have a lot of side-effects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    metadata,
    user.id,
    user.language,
    isLoading,
    user.loggedIn,
    currentTeam,
    currentStorage,
  ]);

  useEffect(() => {
    if (route?.path) {
      Analytics.trackPageView(
        route.path + location.search,
        user?.id,
        Analytics.getUserTypeContext(user),
        currentTeam,
        auth?.auth0UserId
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route?.path, location.search]);

  useEffect(() => {
    if (
      route.shouldRedirect &&
      route.redirectTo &&
      route.shouldRedirect(user, location.search, account) &&
      !isLoading
    ) {
      route.redirectTo(route.path, user, account);
    }
  }, [route, user, isLoading, account]);

  return (
    <TransferCustomizationProvider>
      <Layout
        route={route}
        isLoading={isLoading || !isAmplitudeReady}
        isMobileWebWallpaperEnabled={useIsMobileWebWallpaperEnabled()}
      />
    </TransferCustomizationProvider>
  );
};
